import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Calendar, DateLocalizer, Views, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'moment/locale/en-gb'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import { Select, message, Col, Row, Button, Space, Modal, notification, Table, Tag, Alert, Typography, Tooltip, Input } from "antd";
import {
  getSeriesSelectData,
  submitScheduleDataV2,
  saveBlockScheduleData,
  saveBlockScheduleTitle,
  loadBlockScheduleData,
  getDistributionPlatforms,
  getEpisodeSelectionData,
  getAmagiCloudPortMediaFeeds,
  getCSVWithAds,
  loadBlockSchedules,
  getSegmentsData,
  updateBlockScheduleCalendarConfig,
  validateMarathonSegments,
  MarathonSegmentIssue,
} from "../../utils";
import dayjs from 'dayjs';
import TimeModal from './TimeModal';
import EpisodeEventModal from './EpisodeEventModal';
import CalendarOptions from './CalenderOptions';
import BlockSchedulePickerModal from './BlockSchedulePickerModal'; 
import type { SeriesEvent, SeriesDropdownValue, EpisodeEvent, Segment, AdConfig } from './types';
import { generateEventId, parseEventId } from './utils';
import { StledSpin } from '../../custom-components';
import { ArrowLeftOutlined, ArrowRightOutlined, CopyOutlined } from '@ant-design/icons';
import './SchedulerCalendar.css';
import { AxiosError } from 'axios'
import { CalendarDiv } from './StyledComponents'
import { PaginationProps } from 'antd/lib'

interface EpisodeScheduleItem  {
  refId: string;
  startTime: moment.Moment;
  endTime: moment.Moment;
  startTimeStr: string;
  endTimeStr: string;
  debugStr?: string;
}

type EpisodeWithDurationResult = {
  [key: string]: {
    [key: string]: number;
  };
}

interface ScheduleDataProcessResult {
  episodesWithDuration: any | null;
  episodeScheduleMap: { [key: string]: any[] };
  startDateMoment: moment.Moment;
  endDateMoment: moment.Moment;
  renderOrder: number[][];
  minuteBlocks: string[][];
}

const { Option } = Select;

const DragAndDropCalendar = withDragAndDrop(Calendar)

// Set calendar week to start on Monday via Moment locale
moment.locale('en-gb')


const formats = {
  dayFormat: 'ddd YYYY-MM-DD', // Week/Day headers (e.g., Tue 8)
  dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: DateLocalizer) =>
    localizer?.format(start, 'dddd, YYYY-MM-DD', culture || '') +
    ' - ' +
    localizer?.format(end, 'dddd, YYYY-MM-DD', culture || ''),
};



type CalendarConfigOverrides = {
  selectedDistributionPlatforms?: string[];
  selectedAmagiCloudPortFeed?: string;
  startDate?: Date;
  endDate?: Date;
  selectedTargetPlatform?: string;
  adConfig?: AdConfig | null;
};

// A save that was held back because it would have created an unnamed row;
// it is resumed once the user names the schedule.
type PendingNamedAction =
  | { kind: 'autosave' }
  | { kind: 'config'; overrides?: CalendarConfigOverrides }
  | { kind: 'export' };

export default function SchedulerCalender() {
  const [showSettingsDrawer, setShowSettingsDrawer] = useState<boolean>(false);
  // const [showEpisodeSelectionOnCalender, setShowEpisodeSelectionOnCalender] = useState<boolean>(false);
  const [displayMode, setDisplayMode] = useState<string>('Series'); // 'Episodes', 'Segments', 'Series'
  const [distributionPlatforms, setDistributionPlatforms] = useState<any[]>([]);
  const [targetPlatforms, setTargetPlatforms] = useState<string[]>(['Wurl', 'Amagi FAST', 'Amagi OTA', 'Marathon FAST']);
  const [selectedTargetPlatform, setSelectedTargetPlatform] = useState<string>('Wurl');
  const [selectedDistributionPlatforms, setSelectedDistributionPlatforms] = useState<string[]>([]);
  const [selectedDistributionPlatformsNames, setSelectedDistributionPlatformsNames] = useState<string[]>([]);
  // Marathon FAST grid validation (episodes with missing/partial Marathon segments)
  const [isValidatingEpisodes, setIsValidatingEpisodes] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationIssues, setValidationIssues] = useState<MarathonSegmentIssue[]>([]);
  const [validationChecked, setValidationChecked] = useState(0);
  // true when the dialog was raised by the pre-export gate (export was blocked)
  const [validationBlockedExport, setValidationBlockedExport] = useState(false);
  const [selectedAmagiCloudPortFeed, setSelectedAmagiCloudPortFeed] = useState<string>('');
  const [amagiCloudPortFeeds, setAmagiCloudPortFeeds] = useState<any[]>([]);
  const [selectedCloudPortFeedName, setSelectedCloudPortFeedName] = useState<string>('');
  const [currentDateRange, setCurrentDateRange] = useState<Date[]>([])
  const [currentWeekDateRange, setCurrentWeekDateRange] = useState<Date[]>([])
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showBlockSchedulePickerModal, setShowBlockSchedulePickerModal] = useState(false);
  const [timeModalInitialData, setTimeModalInitialData] = useState<SeriesEvent | null>(null);
  const [seriesDropdownValues, setSeriesDropdownValues] = useState<SeriesDropdownValue[]>([]);
  const [isSeriesValuesLoaded, setIsSeriesValuesLoaded] = useState<boolean>(false);
  const [seriesMap, setSeriesMap] = useState<{ [key: string]: string }>({});
  const [startDate, setStartDate] = useState<Date>(moment().startOf('month').toDate());
  const [endDate, setEndDate] = useState<Date>(moment().endOf('month').toDate());
  const [isDebugMode, setIsDebugMode] = useState<boolean>(false);
  const [defaultDate, setDefaultDate] = useState<Date>(moment().startOf('month').toDate());
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isEditModeOn, setIsEditModeOn] = useState<boolean>(false);
  const [currentBlockScheduleId, setCurrentBlockScheduleId] = useState<string | null>(null);
  const [currentBlockScheduleTitle, setCurrentBlockScheduleTitle] = useState<string | null>(null);
  // Latest id for async callbacks that must not act on a schedule the user has since left.
  const currentBlockScheduleIdRef = useRef<string | null>(null);
  currentBlockScheduleIdRef.current = currentBlockScheduleId;
  // True while editing a fresh draft started via New (or after deleting the
  // loaded schedule). Stops the load effect from pulling in 'Latest' while the
  // draft has no id yet, and from reloading it once its first save assigns one.
  const [isNewDraft, setIsNewDraft] = useState<boolean>(false);
  // Schedule-name popup: 'new' = New button, 'name' = a save hit an unnamed draft.
  const [scheduleNameModal, setScheduleNameModal] = useState<'new' | 'name' | null>(null);
  const [scheduleNameInput, setScheduleNameInput] = useState<string>('');
  const [pendingNamedAction, setPendingNamedAction] = useState<PendingNamedAction | null>(null);
  const [myEvents, setMyEvents] = useState<any[]>([]);
  const [myEpisodeEvents, setMyEpisodeEvents] = useState<any[]>([]);
  const [mySegmentEvents, setMySegmentEvents] = useState<any[]>([]);
  const [showEpisodeModal, setShowEpisodeModal] = useState<boolean>(false);
  const [episodeModalInitialData, setEpisodeModalInitialData] = useState<EpisodeEvent | null>(null);
  const [openConfigureAdsModal, setOpenConfigureAdsModal] = useState(false);
  const [adConfig, setAdConfig] = useState<AdConfig | null>(null);
  const [seriesDataByDate, setSeriesDataByDate] = useState<{ [date: string]: { seriesStart: string, seriesEnd: string, seriesTitle: string, id: string, seriesId: string }[] }>({});
  const [calendarStartAt, setCalendarStartAt] = useState<'12:00 AM' | '06:00 AM'>('12:00 AM');
  const [currentView, setCurrentView] = useState<'week' | 'day'>(Views.WEEK);
  const [modalSchedules, setModalSchedules] = useState<any[]>([]);
  const [eventMode, setEventMode] = useState<'Series' | 'Paid Programming'>('Series');
  const [mediaList, setMediaList] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>({});
  const [isConfigSaving, setIsConfigSaving] = useState<boolean>(false);
  const [isConfigDirty, setIsConfigDirty] = useState<boolean>(false);

  useEffect(() => {
    getDistributionPlatforms((result) => {
      setDistributionPlatforms(result);
    }, (error) => {
      console.error('Error loading distribution platforms', error);
      message.error('Error loading distribution platforms');
    });

    // Load Amagi CloudPort Feeds
    getAmagiCloudPortMediaFeeds((result) => {
      setAmagiCloudPortFeeds(result);
      // Set default feed if available - always default to first if no existing selection
      if (result.length > 0) {
        const existingFeedId = selectedAmagiCloudPortFeed || result[0].feed_id;
        const feedToSelect = result.find(feed => feed.feed_id === existingFeedId) || result[0];
        setSelectedAmagiCloudPortFeed(feedToSelect.feed_id);
        setSelectedCloudPortFeedName(feedToSelect.feed_name);
      }
    }, (error) => {
      console.error('Error loading Amagi CloudPort feeds', error);
      message.error('Error loading Amagi CloudPort feeds');
    });

    getSegmentsData(setMediaList, setLoading, pagination, setPagination, {
      segmentType: "media",
    });
  }, []);

  // Default to first feed if no feed is selected and feeds are available
  // useEffect(() => {

  //   if (amagiCloudPortFeeds.length > 0 && !selectedAmagiCloudPortFeed) {
  //     setSelectedAmagiCloudPortFeed(amagiCloudPortFeeds[0].feed_id);
  //     setSelectedCloudPortFeedName(amagiCloudPortFeeds[0].feed_name);
  //   }
  // }, [amagiCloudPortFeeds, selectedAmagiCloudPortFeed]);

  useEffect(() => {
      // load default events from the server using the block schedule id
      if(isNewDraft) {
        return; //new Draft is getting prepared
      }
      // Wait for reference data to load before processing block schedule
      if(distributionPlatforms.length === 0 || amagiCloudPortFeeds.length === 0) {
        return;
      }
      const blockScheduleId = currentBlockScheduleId || 'Latest';
      setLoading(true);
      loadBlockScheduleData(blockScheduleId, (result) => {
        const events: any[] = [];
        for(let i=0; i < result.seriesSchedule.length; i++) {
          const series = result.seriesSchedule[i];
          // 2025-05-01-11:00
          const start = moment(series.start, 'YYYY-MM-DD-HH:mm').toDate();
          const end = moment(series.end, 'YYYY-MM-DD-HH:mm').toDate();
          // Check if this is a paid programming event (has eventType or mediaId)
          const isPaidProgramming = series.eventType === 'Paid Programming' || series.mediaId;
          const event = {
            id: generateEventId(series.seriesId, start, end),
            title: series.title || series.mediaId || series.seriesId,
            start: start,
            end: end,
            code: series.series_code,
            eventType: series.eventType || (isPaidProgramming ? 'Paid Programming' : 'Series'),
            mediaId: series.mediaId || (isPaidProgramming ? series.seriesId : undefined),
            seriesId: series.seriesId
          };
          // console.log('event', event);
          events.push(event);
        }
        setCurrentBlockScheduleId(result.id);
        setCurrentBlockScheduleTitle(result.title);
        setMyEvents(events);
        // Distribution partners: prefer stored ids (rename-proof), fall back to
        // display names for legacy rows saved before platform_ids existed.
        const storedPlatformIds: string[] = (result.platform_ids || '')
          .split(',')
          .map((id: string) => id.trim())
          .filter(Boolean);
        const validStoredPlatformIds = storedPlatformIds.filter((id: string) =>
          distributionPlatforms.some((platform: any) => platform.id === id)
        );
        let distributionPlatformIds: string[];
        if (validStoredPlatformIds.length > 0) {
          distributionPlatformIds = validStoredPlatformIds;
        } else {
          const selectedDistributionPlatformNames = (result.platform || '')
            .split(',')
            .map((platform: string) => platform.trim())
            .filter(Boolean);
          distributionPlatformIds = distributionPlatforms
            .filter((platform: any) => selectedDistributionPlatformNames.includes(platform.name))
            .map((platform: any) => platform.id);
        }
        const distributionPlatformNames = distributionPlatformIds
          .map((id: string) => distributionPlatforms.find((platform: any) => platform.id === id)?.name)
          .filter(Boolean) as string[];

        // Feed: prefer stored feed_id, fall back to channel name for legacy rows.
        // Do NOT silently fall back to feeds[0] on a miss — that hid renames.
        let selectedAmagiCloudPortFeedId = '';
        if (result.feed_id && amagiCloudPortFeeds.some(feed => feed.feed_id === result.feed_id)) {
          selectedAmagiCloudPortFeedId = result.feed_id;
        } else if (result.channel) {
          selectedAmagiCloudPortFeedId = amagiCloudPortFeeds.find(feed => feed.feed_name === result.channel)?.feed_id || '';
        }
        const selectedFeedName = amagiCloudPortFeeds.find(feed => feed.feed_id === selectedAmagiCloudPortFeedId)?.feed_name || '';
        if ((result.feed_id || result.channel) && !selectedAmagiCloudPortFeedId) {
          message.warning('The CloudPort feed saved on this schedule could not be found (it may have been renamed or removed). Please re-select a feed before exporting.');
        }

        setSelectedDistributionPlatformsNames(distributionPlatformNames);
        setStartDate(moment(result.startDate, 'YYYY-MM-DD').startOf('day').toDate());
        setEndDate(moment(result.endDate, 'YYYY-MM-DD').endOf('day').toDate());
        setDefaultDate(moment(result.startDate, 'YYYY-MM-DD').startOf('month').toDate());
        setSelectedDistributionPlatforms(distributionPlatformIds);
        setSelectedAmagiCloudPortFeed(selectedAmagiCloudPortFeedId);
        setSelectedCloudPortFeedName(selectedFeedName);
        setSelectedTargetPlatform(result.target_platform || targetPlatforms[0] || 'Wurl');
        setIsDebugMode(result.debug_mode);
        setAdConfig(result.ad_config);
        setIsConfigDirty(false);

        const episodeEvents: any[] = [];
        for(let i=0; i < result.episodeSchedule.length; i++) {
          const episode = result.episodeSchedule[i];
          const start = moment(episode.start, 'YYYY-MM-DD-HH:mm').toDate();
          const end = moment(episode.end, 'YYYY-MM-DD-HH:mm').toDate();
          const event = {
            id: generateEventId(episode.refId, start, end),
            title: episode.refId,
            seriesId: episode.seriesId,
            episodeId: episode.episodeId,
            start: start,
            end: end,
            segments: episode.segments || [],
          };
          // console.log('event', event);
          episodeEvents.push(event);
        }
        setMyEpisodeEvents(episodeEvents);

        const segmentEvents = [];
        if(result.segmentSchedule && result.segmentSchedule.length > 0) {
          for(let i=0; i < result.segmentSchedule.length; i++) {
            const segment = result.segmentSchedule[i];
            const start = moment(segment.start, 'YYYY-MM-DD-HH:mm').toDate();
            const end = moment(segment.end, 'YYYY-MM-DD-HH:mm').toDate();
            const event = {
              id: generateEventId(segment.segmentIdentifier, start, end),
              title: segment.segmentIdentifier,
              start: start,
              end: end,
            };
            segmentEvents.push(event);
          }
        }
        setMySegmentEvents(segmentEvents);
        setLoading(false);
      }, (error) => {
        console.error('Error loading block schedule data', error);
        message.error('Error loading block schedule data');
        setLoading(false);
      });
    }, [currentBlockScheduleId, distributionPlatforms, amagiCloudPortFeeds]);
    
  useEffect(() => {
    if(currentBlockScheduleId) {
      setIsEditModeOn(true);
    }
  }, [startDate, endDate]);
  
  useEffect(() => {
    runAutoSave();
  }, [myEvents, myEpisodeEvents, mySegmentEvents]);

  // Resume the save that was held back for a name (see requireScheduleName).
  useEffect(() => {
    if (!pendingNamedAction || needsScheduleName) return;
    const action = pendingNamedAction;
    setPendingNamedAction(null);
    if (action.kind === 'autosave') runAutoSave();
    else if (action.kind === 'config') handleSaveCalendarConfigByBlockId(action.overrides);
    else handleSubmit();
  }, [currentBlockScheduleTitle]);

  useEffect(() => {
    if (!isSeriesValuesLoaded) {
      getSeriesSelectData(setSeriesDropdownValues, setSeriesMap, setIsSeriesValuesLoaded); 
    }
  }, [isSeriesValuesLoaded]);

  useEffect(() => {
    setDefaultDate(moment(startDate).toDate());
  }, [startDate]);

  useEffect(() => {
    // console.log('startDate', startDate);
    // console.log('endDate', endDate);
    // console.log('defaultDate', defaultDate);
    // Prev/Next are always available — navigation is no longer clamped to the
    // configured start/end date range.

    // loop currentDateRange
    const currentWeekStart = moment(defaultDate).startOf('week').toDate();
    const currentWeekEnd = moment(defaultDate).endOf('week').toDate();
    const currentWeekDateRange = [];
    for(let i = 0; i < 7; i++) {
      const dt = moment(currentWeekStart).add(i, 'days').toDate();
      currentWeekDateRange.push(dt);
    }
    setCurrentWeekDateRange(currentWeekDateRange);
    // Filter series data to only include events within startDate and endDate and group by date
    const filteredEvents = myEvents
      .filter(event => {
        return event.start >= moment(startDate).startOf('day').toDate() && event.end <= moment(endDate).endOf('day').toDate();
      })
      .sort((a, b) => {
        const startA = new Date(a.start).getTime();
        const startB = new Date(b.start).getTime();
        if (startA === startB) {
          // tie-breaker: sort by end
          return new Date(a.end).getTime() - new Date(b.end).getTime();
        }
        return startA - startB;
      });

    // Group events by date
    const dateWiseSeriesData: { [date: string]: any[] } = {};
    filteredEvents.forEach(event => {
      const eventDate = moment(event.start).format('YYYY-MM-DD');
      if (!dateWiseSeriesData[eventDate]) {
        dateWiseSeriesData[eventDate] = [];
      }
      dateWiseSeriesData[eventDate].push({
        ...event,
        seriesId: event.id.split('_')[0],
        seriesStart: moment(event.start).format('HH:mm:ss').concat(":00"),
        seriesEnd: moment(event.end).format('HH:mm:ss').concat(":00"),
        seriesTitle: event.title || seriesMap[event.id.split('_')[0]] || event.id.split('_')[0]
      });
    });

    // Set the date-wise series data directly as an object
    setSeriesDataByDate(dateWiseSeriesData);
  }, [defaultDate, startDate, endDate, myEvents, seriesMap]);

  const localizer = momentLocalizer(moment)

  // Calendar visible time window. Keep events anchored to their actual day.
  const isShiftedStart = calendarStartAt === '06:00 AM';
  const minTime = moment('1970-01-01').set({ hour: isShiftedStart ? 6 : 0, minute: 0, second: 0, millisecond: 0 }).toDate();
  // Do not wrap past midnight; max must be later than min for react-big-calendar
  const maxTime = moment('1970-01-01').set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toDate();

  const runAutoSave = async () => {
    // Save episodes even if only episodes exist (for when Repopulate Episodes is clicked)
    if((myEvents.length > 0 || myEpisodeEvents.length > 0) && isEditModeOn) {
      if (requireScheduleName({ kind: 'autosave' })) return;
      if(runValidations() && (displayMode === 'Series' || displayMode === 'Episodes' || displayMode === 'Segments')) {
        const seriesSchedule = myEvents.map((event: any) => {
          const parsed = parseEventId(event.id);
          return {
            ...parsed,
            eventType: event.eventType || 'Series',
            mediaId: event.mediaId,
            seriesId: event.seriesId || parsed.seriesId
          };
        });
        const episodeSchedule = myEpisodeEvents.map((event: any) => {
          const parsedEvent = parseEventId(event.id);
          // rename key seriesId to refId
          return {
            refId: parsedEvent.objId,
            start: parsedEvent.start,
            end: parsedEvent.end,
            seriesId: event.seriesId || '',
            episodeId: event.episodeId || '',
          };
        });

        const segmentSchedule = mySegmentEvents.map((event: any) => {
          const parsedEvent = parseEventId(event.id);
          return {
            segmentIdentifier: parsedEvent.objId,
            start: parsedEvent.start,
            end: parsedEvent.end,
          };
        });

        const blockData = {
          blockScheduleId: currentBlockScheduleId,
          // Only used when this save creates the row (new draft); the backend
          // never changes the title of an existing schedule here.
          title: currentBlockScheduleTitle || 'Untitled',
          seriesSchedule,
          episodeSchedule,
          segmentSchedule,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
        }
        
        setLoading(true);
        saveBlockScheduleData(blockData, (result) => {
          // console.log('saveBlockScheduleData result', result);
          // result.blockScheduleId
          setCurrentBlockScheduleId(result.blockScheduleId);
          setLoading(false);
          // Don't regenerate segments here - only save the data
          // Segments should only be regenerated when explicitly requested (Repopulate Episodes button)
        }, (error) => {
          message.error('Error saving data');
          setLoading(false);
        });

        setIsEditModeOn(false);
      } else if(!runValidations()) {
        message.error('Please correct the errors!');
      }
    }
  };

  const onRangeChange = useCallback((range: any) => {
    // console.log('onRangeChange', range);
    // setCurrentDateRange(range); 

  }, []);


  const addOrUpdateSeriesAsEvent = function (series: SeriesEvent) {
    const { id, oldEventId, start, end, seriesId, eventType, mediaId } = series;
    const eventIdParts = id.split('_');
    const objId = eventIdParts[0];
    const event = {
      id: id,
      title: eventType === 'Paid Programming' 
        ? (mediaId || objId) 
        : (seriesMap[objId] || objId),
      start: start,
      end: end,
      eventType: eventType || 'Series',
      mediaId: mediaId,
      seriesId: seriesId || objId
    };
    console.log('addOrUpdateSeriesAsEvent', event);
    setMyEvents((prev: any) => {
      const existing = prev.find((ev: any) => ev.id === id) ?? {}
      const filtered = prev.filter((ev: any) => ev.id !== id && ev.id !== oldEventId);
      return [...filtered, { ...existing, ...event }]
    })
  };

  const updateEventStartEnd = function (event: any, start: Date | string, end: Date | string) {
    const { seriesId } = parseEventId(event.id);

      setMyEvents((prev: any) => {
        const existing = prev.find((ev: any) => ev.id === event.id) ?? {}
        const filtered = prev.filter((ev: any) => ev.id !== event.id)
        const existingUpdated = {
          ...existing,
          start: typeof start === 'string' ? new Date(start) : start,
          end: typeof end === 'string' ? new Date(end) : end,
        };
        existingUpdated.id = generateEventId(seriesId, existingUpdated.start, existingUpdated.end);
        const updated = [ ...filtered, existingUpdated ];
        console.log('updated', updated);
        return updated;
      });
  }

  const moveEvent = useCallback(
    ({ event, start, end, isAllDay: droppedOnAllDaySlot = false }: { event: any; start: string | Date; end: string | Date; isAllDay?: boolean }) => {
      if(displayMode !== 'Series') {
        return; // do not allow moving events in episode selection mode
      }
      setIsEditModeOn(true);
      const { allDay } = event
      if (!allDay && droppedOnAllDaySlot) {
        event.allDay = true
      }
      if (allDay && !droppedOnAllDaySlot) {
          event.allDay = false;
      }
      updateEventStartEnd(event, start, end);
    },
    [setMyEvents]
  )

  const resizeEvent = useCallback(
    ({ event, start, end }: { event: any; start: string | Date; end: string | Date }) => {
      if((displayMode !== 'Series')){
        return; // do not allow moving events in episode selection mode
      }
      setIsEditModeOn(true);
      updateEventStartEnd(event, start, end);
    },
    [setMyEvents]
  )

  const handleSelectEvent = (event: any) => {
    if(runValidations() && displayMode === 'Series'){
      setIsEditModeOn(true);

      const eventType = event.eventType || 'Series';
      const series: SeriesEvent = {
        id: event.id,
        seriesId: event.seriesId || event.id.split('_')[0],
        start: event.start,
        end: event.end,
        startHourString: moment(event.start).format('HH:mm'),
        endHourString: moment(event.end).format('HH:mm'),
        eventType: eventType,
        mediaId: event.mediaId,
      }
      
      // Set the event mode based on the event type
      setEventMode(eventType);
      setTimeModalInitialData(series);
      setShowTimeModal(true);
    } else if(displayMode === 'Episodes') {
      // Show EpisodeEventModal
      const episodeEvent: EpisodeEvent = {
        id: event.id,
        oldEventId: event.id, // keep the old event id for deletion
        seriesId: event.seriesId,
        episodeId: event.episodeId || '',
        start: event.start,
        end: event.end,
        startHourString: moment(event.start).format('HH:mm'),
        endHourString: moment(event.end).format('HH:mm'),
        segments: event.segments || [],
        selectedDistributionPlatforms: selectedDistributionPlatforms,
      }
      setEpisodeModalInitialData(episodeEvent);
      setShowEpisodeModal(true);
    }
  }

  const handleSelectSlot = (slotInfo: any) => {
    if(!runValidations() || (displayMode !== 'Series')) { return; }
    setIsEditModeOn(true);

    const start = slotInfo.start
    
    
    const startHourHHMM = moment(start).format('HH:mm');
    let end = slotInfo.end
    let endHourHHMM = moment(end).format('HH:mm');
    if(startHourHHMM === '00:00' && endHourHHMM === '00:00') {
      endHourHHMM = '23:59';
      end = moment(start).endOf('day').toDate();
    }
    // render SeriesInputDialog 
    const series: SeriesEvent = {
      id: '',
      seriesId: '',
      start: start,
      end: end,
      startHourString: startHourHHMM,
      endHourString: endHourHHMM,
    }
    // console.log('handleSelectSlot', series);
    setTimeModalInitialData(series);
    setShowTimeModal(true);
  }

  const runValidations = () => {
    if(!startDate || !endDate) {
      console.log('startDate', startDate);
      message.error('Please select start and end date');
      return false;
    }
    if(moment(startDate).isAfter(endDate)) {
      console.log('endDate', endDate);
      message.error('Start date must be before end date');
      return false;
    }
    return true;
  }

  const handleSubmit = async () => {
    if (requireScheduleName({ kind: 'export' })) return;
    try {
      if(runValidations()) {
          // Ad configuration is required on every platform that runs the shared
          // FAST/OTA exporter — Marathon FAST included, since it moved onto the
          // Amagi FAST export rules.
          if (selectedTargetPlatform === 'Amagi FAST' || selectedTargetPlatform === 'Amagi OTA' || selectedTargetPlatform === 'Marathon FAST') {
            const adPacingValue = selectedTargetPlatform === 'Amagi OTA' ? adConfig?.numberOfSegments : adConfig?.minutesOfAdsPerHour;
            if (!adConfig || !adConfig.selectedAds.length || !adPacingValue || (selectedTargetPlatform === 'Amagi OTA' && !adConfig.program)) {
              message.info(`Please configure ads for ${selectedTargetPlatform}.`);
              setOpenConfigureAdsModal(true);
              return;
            }
          }

        const seriesSchedule = myEvents.map((event: any) => {
          const parsed = parseEventId(event.id);
          return {
            ...parsed,
            eventType: event.eventType || 'Series',
            mediaId: event.mediaId,
            seriesId: event.seriesId || parsed.seriesId
          };
        });
        if(myEpisodeEvents.length == 0) {
          message.error('Please click "Repopulate Episodes" to load episodes from the pool first');
          return;
        }

        // Pre-export gate (Marathon FAST only): a Marathon FAST CSV silently drops
        // segment-less episodes and truncates partial ones, so verify the grid
        // first. Real problems block the export (fix / remove, then re-export); a
        // validation outage is non-fatal — warn and let the export proceed.
        if (selectedTargetPlatform === 'Marathon FAST') {
          const refIds = gridEpisodeRefIds();
          setLoading(true);
          let issues: MarathonSegmentIssue[] = [];
          try {
            issues = await validateMarathonForRefs(refIds);
          } catch (err: any) {
            console.error('Pre-export Marathon validation failed', err);
            message.warning('Could not validate Marathon segments before export — proceeding without the pre-check.');
            issues = [];
          }
          setLoading(false);
          if (issues.length > 0) {
            setValidationChecked(refIds.length);
            setValidationIssues(issues);
            setValidationBlockedExport(true);
            setShowValidationModal(true);
            return;
          }
        }

        const episodeSchedule = myEpisodeEvents.map((event: any) => {
          const parsedEvent = parseEventId(event.id);
          // rename key seriesId to refId
          return {
            refId: parsedEvent.objId,
            start: parsedEvent.start,
            end: parsedEvent.end,
            segments: event.segments || [] // Include segments in the payload
          };
        });
        // Collect all segments from episodeSchedule for separate segments array (for paid programming)
        const allSegments: any[] = [];
        episodeSchedule.forEach((episode: any) => {
          if (episode.segments && Array.isArray(episode.segments)) {
            episode.segments.forEach((seg: any) => {
              // Ensure segment has all required fields for backend
              const segmentData = {
                segment_identifier: seg.segmentIdentifier || seg.segment_identifier || `${episode.refId}_${seg.segmentNumber || seg.segment_num || 0}`,
                segmentIdentifier: seg.segmentIdentifier || seg.segment_identifier || `${episode.refId}_${seg.segmentNumber || seg.segment_num || 0}`,
                duration: seg.segmentDuration || seg.duration || "00:00:00:00",
                segmentDuration: seg.segmentDuration || seg.duration || "00:00:00:00",
                segment_num: seg.segmentNumber || seg.segment_num || 0,
                segmentNumber: seg.segmentNumber || seg.segment_num || 0,
                offset: seg.offset || "",
                asset_identifier: seg.asset_identifier || "",
                segment_type: seg.segment_type || "episode",
                media_type: seg.media_type || "",
                title: seg.title || "",
                series_code: seg.series_code || "",
                ref_id: seg.ref_id || episode.refId || "",
                parent_feed: seg.parent_feed || "",
                series_id: seg.series_id || null,
                episode_id: seg.episode_id || null,
                isPaidProgramming: seg.isPaidProgramming || false,
              };
              allSegments.push(segmentData);
            });
          }
        });
        
        const data = {
          blockScheduleId: currentBlockScheduleId,
          title: currentBlockScheduleTitle || 'Untitled',
          seriesSchedule,
          episodeSchedule,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          isDebugMode,
          selectedTargetPlatform,
          selectedAmagiCloudPortFeed,
          selectedDistributionPlatforms, // used by OTA PP gap-fill to only pull fillers from selected distribution partner(s)
          ...(selectedTargetPlatform !== 'Wurl' && adConfig), // Include adConfig only if target platform is not Wurl
          ...(selectedTargetPlatform !== 'Wurl' && { seriesBlockEvents: seriesDataByDate }), // Include seriesBlockEvents only if target platform is not Wurl
          ...(allSegments.length > 0 && { segments: allSegments }) // Include segments array for paid programming
        }
        // Log filtered states separately (without changing payloads)
        const fileType: Record<string, string> = {
          'Wurl': 'csv',
          'Amagi FAST': 'zip',
          'Amagi OTA': 'zip',
          // Marathon FAST delivers ONE flat CSV covering the whole date range
          // (Amagi FAST sends a ZIP of per-day CSVs).
          'Marathon FAST': 'csv'
        }
        const fileStartDate = moment(data?.startDate, "YYYY-MM-DD").format("MM-DD-YYYY");
        const fileEndDate = moment(data?.endDate, "YYYY-MM-DD").format("MM-DD-YYYY");
        const targetPlatformForFileName = selectedTargetPlatform || targetPlatforms[0] || 'Wurl';
        const fileName = `${isDebugMode ? '[DEBUG] ' : ''}${targetPlatformForFileName}_${selectedCloudPortFeedName}_${fileStartDate}_${fileEndDate}.${fileType[targetPlatformForFileName as keyof typeof fileType]}`;
        setLoading(true);
        // Marathon FAST exports through the same async Lambda as Amagi: it returns
        // a ZIP of per-day CSVs by email, validated on the way out, instead of
        // streaming a single CSV straight to the browser.
        if (selectedTargetPlatform === 'Amagi FAST' || selectedTargetPlatform === 'Amagi OTA' || selectedTargetPlatform === 'Marathon FAST') {
          getCSVWithAds(data, (jobResponse: any) => {
            setLoading(false);
            const msg =
              jobResponse?.message ||
              `${selectedTargetPlatform} export is being processed. You will receive it by email when it is ready.`;
            notification.success({
              message: 'Export started',
              description: msg,
              duration: 10,
            });
          }, (error: any) => {
            setLoading(false);
            message.error('Error submitting export request. Please try again.');
            console.error('Error submitting CSV data for ' + selectedTargetPlatform + ': ', error);
          });
        } else {
          submitScheduleDataV2(data, (csvContent) => {
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success('CSV downloaded successfully');
            setLoading(false);
          }, (error) => {
            message.error('Error submitting data');
            console.log('Error submitting CSV data: ', error);
            setLoading(false);
          });
        }


      } 
      else {
        message.error('Please correct the errors!');
      }
    } catch (error) {
      console.error('Error in handleSubmit', error);
      message.error('Error generating CSV.');
    }
  }

  const handleOnNavigate = (newDate: Date) => {
    // console.log('handleOnNavigate', newDate);
    setDefaultDate(newDate);
  }

  const handleCopyOverEventsFromLastWeek = () => {
    // console.log('currentWeekDateRange', currentWeekDateRange);
    setIsEditModeOn(true);
    for(let i = 0; i < currentWeekDateRange.length; i++) {
      const dt = currentWeekDateRange[i];
      const prevWeekDt = moment(dt).subtract(7, 'days');
      const prevWeekDtDayStart = moment(prevWeekDt).startOf('day').toDate();
      const prevWeekDtDayEnd = moment(prevWeekDt).endOf('day').toDate();
      const eventsToCopy = myEvents.filter((event: any) => {
        return event.start >= prevWeekDtDayStart && event.end <= prevWeekDtDayEnd;
      });
      const newEvents = eventsToCopy.map((event: any) => {
        const newEvent = { ...event };
        newEvent.start = moment(event.start).add(7, 'days').toDate();
        newEvent.end = moment(event.end).add(7, 'days').toDate();
        newEvent.id = generateEventId(newEvent.id.split('_')[0], newEvent.start, newEvent.end);
        return newEvent;
      });
      setMyEvents((prev: any) => {
        return [...prev, ...newEvents];
      });
    }
    message.success('Events copied over successfully');
  }

  const movePrevWeek = () => {
    const newDate = moment(defaultDate).subtract(7, 'days').toDate();
    setDefaultDate(newDate);
  }

  const moveNextWeek = () => {
    const newDate = moment(defaultDate).add(7, 'days').toDate();
    setDefaultDate(newDate);
  }

  const outsideRangeDayPropGetter = (date: Date) => {
    // if date is before startDate or after endDate, return grey background
    const isOutsideRange = moment(date).isBefore(startDate) || moment(date).isAfter(endDate);
    return {
      style: {
        backgroundColor: isOutsideRange ? '#f0f0f0' : '',
        // In 06:00 AM mode, visually emphasize post-midnight region (12:00 AM - 5:59 AM) as belonging to the previous day
        ...(isShiftedStart && moment(date).hour() < 6 ? { opacity: 0.9 } : {}),
      },
    };
  }
  const handleDistributionPlatformsChange = (values: string[]) => {
    setSelectedDistributionPlatforms(values);
    setSelectedDistributionPlatformsNames(values.map(value => distributionPlatforms.find(platform => platform.id === value)?.name || ''));
  }
  const handleAmagiCloudPortFeedChange = (value: string) => {
    setSelectedAmagiCloudPortFeed(value);
    setSelectedCloudPortFeedName(amagiCloudPortFeeds.find(feed => feed.feed_id === value)?.feed_name || 'UnknownFeed');
  }
  const handleTargetPlatformChange = (value: string) => {
    setSelectedTargetPlatform(value);
    // Reset ad config when switching away from Amagi FAST
    // if (value !== 'Amagi FAST' && value !== 'Amagi OTA') {
    //   setAdConfig(null);
    // }
  }

  const markCalendarConfigDirty = () => {
    setIsConfigDirty(true);
  }

  const handleSaveCalendarConfigByBlockId = (overrides?: CalendarConfigOverrides) => {
    // No early return when there is no schedule yet: a brand-new schedule can be
    // configured before any grid events exist. The backend creates the row from
    // this config and returns the new id (see below).
    if (requireScheduleName({ kind: 'config', overrides })) return;
    const effectiveDistributionPlatforms = overrides?.selectedDistributionPlatforms ?? selectedDistributionPlatforms;
    const effectiveFeedId = overrides?.selectedAmagiCloudPortFeed ?? selectedAmagiCloudPortFeed;
    const effectiveStartDate = overrides?.startDate ?? startDate;
    const effectiveEndDate = overrides?.endDate ?? endDate;
    const effectiveTargetPlatform = overrides?.selectedTargetPlatform ?? selectedTargetPlatform;
    const effectiveAdConfig = overrides?.adConfig !== undefined ? overrides.adConfig : adConfig;

    const distributionNames = effectiveDistributionPlatforms
      .map((id) => distributionPlatforms.find((platform: any) => platform.id === id)?.name)
      .filter(Boolean) as string[];
    const effectiveFeedName = amagiCloudPortFeeds.find((feed: any) => feed.feed_id === effectiveFeedId)?.feed_name || '';

    const payload = {
      blockScheduleId: currentBlockScheduleId,
      title: currentBlockScheduleTitle || 'Untitled',
      // Send canonical date-only strings to avoid timezone day-shift on serialization.
      startDate: moment(effectiveStartDate).format("YYYY-MM-DD"),
      endDate: moment(effectiveEndDate).format("YYYY-MM-DD"),
      channel: effectiveFeedName,
      platform: distributionNames.join(', '),
      // Authoritative, rename-proof foreign refs (channel/platform keep display names).
      feedId: effectiveFeedId || null,
      platformIds: effectiveDistributionPlatforms.join(', '),
      targetPlatform: effectiveTargetPlatform,
      adConfig: effectiveAdConfig,
      debugMode: isDebugMode,
    };
    setIsConfigSaving(true);
    updateBlockScheduleCalendarConfig(
      payload,
      (result) => {
        if (result?.blockScheduleId && result.blockScheduleId !== currentBlockScheduleId) {
          // First save of a brand-new schedule created the row — adopt its id so
          // subsequent autosaves/config saves target the same row.
          setCurrentBlockScheduleId(result.blockScheduleId);
        }
        setSelectedDistributionPlatformsNames(distributionNames);
        setIsConfigDirty(false);
        setIsConfigSaving(false);
        message.success('Calendar configuration saved');
      },
      () => {
        message.error('Failed to update calendar configuration');
        setIsConfigSaving(false);
      }
    );
  }

  const handleDisplayModeChange = (value: string) => {
    // Just change the display mode without making API calls
    // User should explicitly click "Repopulate Episodes" button to load episodes
    // Clear any potential duplicate events when switching modes to prevent overlaps
    if(value === 'Episodes') {
      setDisplayMode('Episodes');
    } else if(value === 'Segments') {
      setDisplayMode('Segments');
      // Ensure segments are deduplicated when switching to Segments view
      setMySegmentEvents((prev: any[]) => {
        const uniqueSegments = new Map();
        prev.forEach((seg: any) => {
          if (!uniqueSegments.has(seg.id)) {
            uniqueSegments.set(seg.id, seg);
          }
        });
        return Array.from(uniqueSegments.values());
      });
    } else if(value === 'Series') {
      setDisplayMode('Series');
    }
  }


  const swapEpisodeEvent = (selectedEpisode: EpisodeEvent) => {
    setIsEditModeOn(true);
    // console.log('swapEpisodeEvent', selectedEpisode);
    const oldEventId = selectedEpisode.oldEventId;
    const newEventId = selectedEpisode.id;
    if(oldEventId && oldEventId !== newEventId) {
      // find old event in myEpisodeEvents
      const oldEventIndex = myEpisodeEvents.findIndex((event: any) => event.id === oldEventId);
      const oldEvent = myEpisodeEvents[oldEventIndex];
      // console.log('oldEvent', oldEvent);
      // construct new event
      const newEvent = {
        id: newEventId,
        title: selectedEpisode.episodeId,
        start: selectedEpisode.start,
        end: selectedEpisode.end,
        seriesId: selectedEpisode.seriesId,
        episodeId: selectedEpisode.episodeId,
        segments: selectedEpisode.segments || [],
      };
      // console.log('newEvent', newEvent);
      // find the duration difference between old and new event
      const oldDuration = moment(oldEvent.end).diff(moment(oldEvent.start), 'seconds');
      const newDuration = moment(newEvent.end).diff(moment(newEvent.start), 'seconds');
      // console.log('oldDuration', oldDuration, 'newDuration', newDuration);
      const durationDifference = newDuration - oldDuration;
      // if duration difference is not zero, adjust all events that fall on same day and that start after the new event's start time
      if(durationDifference !== 0) {
        const adjustedEvents = myEpisodeEvents.map((event: any) => {
          if(event.start > newEvent.start && moment(event.start).isSame(moment(newEvent.start), 'day')) {
            // adjust start and end time by duration difference
            const newStart = moment(event.start).add(durationDifference, 'seconds');
            let newEnd = moment(event.end).add(durationDifference, 'seconds');
            // if end is after midnight, round it to before midnight
            if(newEnd.isAfter(moment(newStart).endOf('day'))) {
              newEnd =  moment(newStart).set({ hour: 23, minute: 58, second: 59 });
            }
            const oldEventIdParts = parseEventId(event.id);
            const newEventId = generateEventId(oldEventIdParts.objId, newStart.toDate(), newEnd.toDate());
            return {
              ...event,
              start: newStart.toDate(),
              end: newEnd.toDate(),
              id: newEventId,
            };
          }
          return event;
        });
        adjustedEvents[oldEventIndex] = newEvent; // replace old event with new event
        setMyEpisodeEvents(adjustedEvents);
      }
      
    } else {
      console.log('no event change detected');
    }
  }

  const slotGroupPropGetter = useCallback(
    () => ({
      style: {
        minHeight: ((displayMode !== 'Series') ? '60px' : '41px'), // Adjust height based on mode
      },
    }),
    [ displayMode ]
  )

  // Distinct episode ref_ids currently placed on the grid.
  const gridEpisodeRefIds = (): string[] =>
    Array.from(
      new Set(
        myEpisodeEvents
          .map((event: any) => parseEventId(event.id).objId)
          .filter(Boolean)
      )
    ) as string[];

  // Promise wrapper around the callback-based validate helper (for the pre-export gate).
  const validateMarathonForRefs = (refIds: string[]): Promise<MarathonSegmentIssue[]> =>
    new Promise((resolve, reject) => {
      validateMarathonSegments(
        refIds,
        selectedDistributionPlatforms,
        (result) => resolve(result.issues || []),
        (err) => reject(err)
      );
    });

  // Copy the problematic episode/video references as TSV so the user can paste
  // them into a sheet / ticket and go fix the underlying assets.
  const copyValidationReferences = () => {
    const header = ['Ref ID', 'Video ID', 'File Name', 'Segments', 'Problem'].join('\t');
    const lines = validationIssues.map((i) => {
      const exp =
        i.expectedMin == null
          ? '?'
          : i.expectedMin === i.expectedMax
          ? `${i.expectedMin}`
          : `${i.expectedMin}-${i.expectedMax}`;
      const problem = i.reasons.join('; ') + (i.segmentationNotes ? ` — ${i.segmentationNotes}` : '');
      return [i.refId, i.videoId ?? '', i.fileName ?? '', `${i.actual}/${exp}`, problem].join('\t');
    });
    const text = [header, ...lines].join('\n');
    navigator.clipboard.writeText(text).then(
      () => message.success(`Copied ${validationIssues.length} reference(s) to clipboard`),
      () => message.error('Could not copy to clipboard')
    );
  };

  // Marathon FAST only: check every distinct episode ref currently on the grid
  // for missing / partial Marathon segments (or segments with empty S3 URLs).
  const handleValidateEpisodesClick = () => {
    const refIds = gridEpisodeRefIds();
    if (refIds.length === 0) {
      message.info('No episodes on the grid to validate — repopulate first.');
      return;
    }
    setIsValidatingEpisodes(true);
    validateMarathonSegments(
      refIds,
      selectedDistributionPlatforms,
      (result) => {
        setIsValidatingEpisodes(false);
        setValidationChecked(result.checked ?? refIds.length);
        if (result.issues && result.issues.length > 0) {
          setValidationBlockedExport(false);
          setValidationIssues(result.issues);
          setShowValidationModal(true);
        } else {
          message.success(`All ${refIds.length} episode(s) have complete Marathon segments.`);
        }
      },
      (error: any) => {
        setIsValidatingEpisodes(false);
        const msg = error?.response?.data?.message || error?.message || 'Validation failed';
        message.error('Failed to validate episodes: ' + msg);
      }
    );
  };

  // Span actually covered by the series drawn on the calendar in Series mode.
  // Repopulate works off this, not the Calendar Options date range — that range
  // is only used when exporting the CSV. Returns null when nothing is scheduled.
  const getScheduledSeriesDateRange = () => {
    if(myEvents.length === 0) {
      return null;
    }
    let earliest = moment(myEvents[0].start);
    let latest = moment(myEvents[0].end);
    myEvents.forEach((event: any) => {
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);
      if(eventStart.isBefore(earliest)) {
        earliest = eventStart;
      }
      if(eventEnd.isAfter(latest)) {
        latest = eventEnd;
      }
    });
    // An event ending exactly at midnight belongs to the previous day.
    if(latest.isSame(moment(latest).startOf('day'))) {
      latest.subtract(1, 'millisecond');
    }
    return {
      start: earliest.startOf('day'),
      end: latest.startOf('day'),
    };
  }

  const handleRepopulateEpisodesClick = async () => {
    const scheduledRange = getScheduledSeriesDateRange();
    if(!scheduledRange) {
      message.error('No series scheduled on the calendar — add series in Series mode first.');
      return;
    }
    setLoading(true);
    setIsEditModeOn(true);
    if(runValidations()) {
      const seriesSchedule = myEvents.map((event: any) => {
        const parsed = parseEventId(event.id);
        return {
          ...parsed,
          eventType: event.eventType || 'Series',
          mediaId: event.mediaId,
          seriesId: event.seriesId || parsed.seriesId
        };
      });
      const data = {
        seriesSchedule,
        // Derived from the scheduled series themselves, not the date-range picker.
        startDate: scheduledRange.start.format('YYYY-MM-DD'),
        endDate: moment(scheduledRange.end).add(1, 'day').format('YYYY-MM-DD'),
        isDebugMode,
        selectedDistributionPlatforms,
        selectedAmagiCloudPortFeed,
        selectedTargetPlatform,
      }
      setLoading(true);
      message.loading('Loading episodes from episode pool...', 5);
      getEpisodeSelectionData(data, (result: any) => {
        const segmentEvents: any[] = [];
        const events: any[] = [];
        result.episodeScheduleMap = result.episodeScheduleMap || {};
        // iterate over result.episodeScheduleMap
        const timeBlockKeys = Object.keys(result.episodeScheduleMap);
        for(let i = 0; i < timeBlockKeys.length; i++) {
          const timeBlockKey = timeBlockKeys[i];
          const episodeScheduleItems = result.episodeScheduleMap[timeBlockKey];
          for(let j = 0; j < episodeScheduleItems.length; j++) {
            const episodeScheduleItem = episodeScheduleItems[j];
            // 2025-05-01T00:00:00.000Z
            episodeScheduleItem.startTime = moment(episodeScheduleItem.startTimeStr, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]');
            episodeScheduleItem.endTime = moment(episodeScheduleItem.endTimeStr, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]');
            
            // Check if this is paid programming - only skip if explicitly marked as paid programming
            // Check the episode schedule item flag first, then check segments
            let isPaidProgramming = false;
            if (episodeScheduleItem.isPaidProgramming === true) {
              isPaidProgramming = true;
            } else if (episodeScheduleItem.segments && Array.isArray(episodeScheduleItem.segments) && episodeScheduleItem.segments.length > 0) {
              // Check if any segment is explicitly marked as paid programming
              const hasPaidProgrammingSegment = episodeScheduleItem.segments.some((seg: any) => 
                seg && typeof seg.isPaidProgramming === 'boolean' && seg.isPaidProgramming === true
              );
              if (hasPaidProgrammingSegment) {
                isPaidProgramming = true;
              }
            }
            
            // Skip paid programming events in episode calendar (keep it blank)
            // Only skip if we have a clear indication it's paid programming
            // For series events, isPaidProgramming will be false, so they will be shown
            if (!isPaidProgramming) {
              const event = {
                id: generateEventId(episodeScheduleItem.refId, episodeScheduleItem.startTime.toDate(), episodeScheduleItem.endTime.toDate()),
                title: `${episodeScheduleItem.refId}`,
                episodeId: episodeScheduleItem.episodeId,
                seriesId: episodeScheduleItem.seriesId,
                start: episodeScheduleItem.startTime.toDate(),
                end: episodeScheduleItem.endTime.toDate(),
              };
              events.push(event);
            }
              // Always create segment events (for both series and paid programming)
              for(let k = 0; k < episodeScheduleItem.segments.length; k++) {
                const segment = episodeScheduleItem.segments[k];
                segment.startTime = moment(segment.startTimeStr, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]');
                segment.endTime = moment(segment.endTimeStr, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]');
                // Check if segment is paid programming - check both segment flag and parent episode flag
                const segmentIsPaidProgramming = segment.isPaidProgramming === true || episodeScheduleItem.isPaidProgramming === true || isPaidProgramming;
                // For paid programming, use title instead of segmentIdentifier
                const segmentTitle = segmentIsPaidProgramming && segment.title 
                  ? segment.title 
                  : segment.segmentIdentifier;
                const segmentEventId = generateEventId(segment.segmentIdentifier, segment.startTime.toDate(), segment.endTime.toDate());
                const segmentEvent = {
                  id: segmentEventId,
                  title: segmentTitle,
                  start: segment.startTime.toDate(),
                  end: segment.endTime.toDate(),
                  isPaidProgramming: segmentIsPaidProgramming, // Mark paid programming segments
                };
                // Deduplicate segments to prevent overlaps
                const existingSegment = segmentEvents.find((se: any) => se.id === segmentEventId);
                if (!existingSegment) {
                  segmentEvents.push(segmentEvent);
                }
              }
          }
        }
        // Deduplicate segment events before setting to prevent overlaps
        const uniqueSegmentEvents = segmentEvents.reduce((acc: any[], current: any) => {
          const existing = acc.find((seg: any) => seg.id === current.id);
          if (!existing) {
            acc.push(current);
          }
          return acc;
        }, []);
        
        setMySegmentEvents(uniqueSegmentEvents);
        setMyEpisodeEvents(events);
        setDisplayMode('Episodes');

        // Save the generated episodes to the database
        // The useEffect watching myEpisodeEvents will trigger runAutoSave, but we'll also save explicitly here
        setIsEditModeOn(true);
        setLoading(false);
      }, (error: AxiosError) => {
        const errorMessage = typeof error.response?.data === 'object' && error.response?.data !== null
          ? (error.response.data as { message?: string }).message
          : error.message;
        message.error('Error loading episode selection data: ' + errorMessage);
        setLoading(false);
      });
    } else {
      message.error('Please correct the errors!');
      setLoading(false);
    }
    loadBlockSchedules(
      (data: any[]) => {
        setModalSchedules(data);
      },
      (error: any) => {
        message.error("Failed to load block schedules");
      }
    );
  }

  // No row exists yet and the draft has no name, so a save would store "Untitled".
  const needsScheduleName = !currentBlockScheduleId && !currentBlockScheduleTitle?.trim();

  // Holds the save back and asks for a name instead; returns true when it did.
  const requireScheduleName = (action: PendingNamedAction) => {
    if (!needsScheduleName) return false;
    setPendingNamedAction(action);
    setScheduleNameModal('name');
    return true;
  };

  const closeScheduleNameModal = () => {
    setScheduleNameModal(null);
    setScheduleNameInput('');
  };

  const handleScheduleNameConfirm = () => {
    const title = scheduleNameInput.trim();
    if (!title) return;
    if (scheduleNameModal === 'new') {
      setPendingNamedAction(null);
      startNewDraft(title);
    } else {
      // The held-back save resumes from the effect that watches the title, once
      // the new title is in state (handlers read it from their render closure).
      setCurrentBlockScheduleTitle(title);
    }
    closeScheduleNameModal();
  };

  const handleScheduleNameCancel = () => {
    setPendingNamedAction(null);
    closeScheduleNameModal();
  };

  // Inline rename from the title on the main page. A draft with no row yet just
  // takes the new name locally; its first save stores it.
  const handleRenameSchedule = (rawTitle: string) => {
    const title = rawTitle.trim();
    // Unchanged — including an unnamed draft left as its "Untitled" placeholder.
    if (!title || title === (currentBlockScheduleTitle || 'Untitled')) return;
    if (!currentBlockScheduleId) {
      setCurrentBlockScheduleTitle(title);
      return;
    }
    const scheduleId = currentBlockScheduleId;
    saveBlockScheduleTitle(
      { id: scheduleId, title },
      () => {
        // Ignore a late response if the user has switched schedules meanwhile.
        if (currentBlockScheduleIdRef.current === scheduleId) setCurrentBlockScheduleTitle(title);
        message.success('Title updated successfully');
      },
      () => {
        message.error('Failed to update title');
      }
    );
  };

  // null title = unnamed draft (shown as "Untitled"; its first save asks for a name).
  const startNewDraft = (title: string | null) => {
    setIsNewDraft(true);
    setIsEditModeOn(false);
    setMyEvents([]);
    // Clear these too, or the previous schedule's episodes/segments would be
    // saved into the new row on its first save.
    setMyEpisodeEvents([]);
    setMySegmentEvents([]);
    setCurrentBlockScheduleTitle(title);
    setCurrentBlockScheduleId(null);
    setStartDate(moment().startOf('month').toDate());
    setEndDate(moment().endOf('month').toDate());
    setDefaultDate(moment().startOf('month').toDate());
    setSelectedDistributionPlatforms([]);
    setSelectedAmagiCloudPortFeed('');
    setSelectedCloudPortFeedName('');
  }

  const handleClearWeek = () => {
    if (currentWeekDateRange.length === 0) return;
    const weekStart = moment(currentWeekDateRange[0]).startOf('day').toDate();
    const weekEnd = moment(currentWeekDateRange[currentWeekDateRange.length - 1]).endOf('day').toDate();
 
    const filteredEvents = myEvents.filter(ev => ev.start < weekStart || ev.start > weekEnd);
    const filteredEpisodeEvents = myEpisodeEvents.filter(ev => ev.start < weekStart || ev.start > weekEnd);
    const filteredSegmentEvents = mySegmentEvents.filter(ev => ev.start < weekStart || ev.start > weekEnd);

    setMyEvents(filteredEvents);
    setMyEpisodeEvents(filteredEpisodeEvents);
    setMySegmentEvents(filteredSegmentEvents);
    // Nothing is stored for an unnamed draft yet, so don't create a row just to clear it.
    if (needsScheduleName) return;

    const seriesSchedule = filteredEvents.map((event: any) => {
      const parsed = parseEventId(event.id);
      return { ...parsed, eventType: event.eventType || 'Series', mediaId: event.mediaId, seriesId: event.seriesId || parsed.seriesId };
    });
    const episodeSchedule = filteredEpisodeEvents.map((event: any) => {
      const parsedEvent = parseEventId(event.id);
      return { refId: parsedEvent.objId, start: parsedEvent.start, end: parsedEvent.end, seriesId: event.seriesId || '', episodeId: event.episodeId || '' };
    });
    const segmentSchedule = filteredSegmentEvents.map((event: any) => {
      const parsedEvent = parseEventId(event.id);
      return { segmentIdentifier: parsedEvent.objId, start: parsedEvent.start, end: parsedEvent.end };
    });

    setLoading(true);
    saveBlockScheduleData(
      { blockScheduleId: currentBlockScheduleId, title: currentBlockScheduleTitle || 'Untitled', seriesSchedule, episodeSchedule, segmentSchedule, startDate: moment(startDate).format('YYYY-MM-DD'), endDate: moment(endDate).format('YYYY-MM-DD') },
      (result: any) => {
        setCurrentBlockScheduleId(result.blockScheduleId);
        setLoading(false);
        message.success('Current week cleared successfully.');
      },
      (_error: any) => {
        message.error('Error clearing week');
        setLoading(false);
      }
    );
  };

  const handleClearSchedule = () => {
    setMyEvents([]);
    setMyEpisodeEvents([]);
    setMySegmentEvents([]);
    // Nothing is stored for an unnamed draft yet, so don't create a row just to clear it.
    if (needsScheduleName) return;

    setLoading(true);
    saveBlockScheduleData(
      { blockScheduleId: currentBlockScheduleId, title: currentBlockScheduleTitle || 'Untitled', seriesSchedule: [], episodeSchedule: [], segmentSchedule: [], startDate: moment(startDate).format('YYYY-MM-DD'), endDate: moment(endDate).format('YYYY-MM-DD') },
      (result: any) => {
        setCurrentBlockScheduleId(result.blockScheduleId);
        setLoading(false);
        message.success('Full schedule cleared successfully.');
      },
      (_error: any) => {
        message.error('Error clearing schedule');
        setLoading(false);
      }
    );
  };

  return (
      <Fragment>
        <CalendarDiv>
          <h1 style={{ textAlign: 'center' }}>Weekly Calendar</h1>
          
          <>
            <StledSpin spinning={isLoading}>
            <CalendarOptions
              currentBlockScheduleTitle={currentBlockScheduleTitle || 'Untitled'}
              setIsEditModeOn={setIsEditModeOn}
              setShowBlockSchedulePickerModal={setShowBlockSchedulePickerModal}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              handleCopyOverEventsFromLastWeek={ handleCopyOverEventsFromLastWeek }
              handleClearWeek={handleClearWeek}
              handleClearSchedule={handleClearSchedule}
              handleRepopulateEpisodesClick={handleRepopulateEpisodesClick}
              handleValidateEpisodesClick={handleValidateEpisodesClick}
              isValidatingEpisodes={isValidatingEpisodes}
              handleSaveCalendarConfigByBlockId={handleSaveCalendarConfigByBlockId}
              isConfigSaving={isConfigSaving}
              isConfigDirty={isConfigDirty}
              markCalendarConfigDirty={markCalendarConfigDirty}
              handleDisplayModeChange={ handleDisplayModeChange }
              handleSubmit={ handleSubmit }
              displayMode={displayMode}
              startDate={ startDate}
              endDate={ endDate }
              selectedTargetPlatform={ selectedTargetPlatform }
              targetPlatforms={ targetPlatforms }
              handleTargetPlatformChange={ handleTargetPlatformChange }
              distributionPlatforms={ distributionPlatforms }
              handleDistributionPlatformsChange={ handleDistributionPlatformsChange }
              selectedDistributionPlatforms={ selectedDistributionPlatforms }
              amagiCloudPortFeeds={amagiCloudPortFeeds}
              selectedAmagiCloudPortFeed={selectedAmagiCloudPortFeed}
              handleAmagiCloudPortFeedChange={handleAmagiCloudPortFeedChange}
              setSelectedAmagiCloudPortFeed={setSelectedAmagiCloudPortFeed}
              setIsDebugMode={ setIsDebugMode }
              isDebugMode={ isDebugMode }
              mySegmentEvents={ mySegmentEvents }
              openConfigureAdsModal={openConfigureAdsModal}
              setOpenConfigureAdsModal={setOpenConfigureAdsModal}
              adConfig={adConfig}
              setAdConfig={setAdConfig}
              onNewScheduleClick={() => setScheduleNameModal('new')}
              onRenameSchedule={handleRenameSchedule}
            /> 
            <Row align="middle" justify="space-between" style={{ margin: '20px 0' }}>
              <Col span={8} style={{ textAlign: 'left' }}>
                {/* <Segmented
                  options={["12:00 AM", "06:00 AM"]}
                  value={calendarStartAt}
                  onChange={(val) => setCalendarStartAt(val as '12:00 AM' | '06:00 AM')}
                /> */}
              </Col>
              <Col span={8}>
                {currentView === Views.DAY && (
                  <Row justify={'center'} align={'middle'}>
                      <Button type="link" onClick={() => setCurrentView(Views.WEEK)}><ArrowLeftOutlined />Back to Week</Button>
                  </Row>
                )}
              </Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <Space direction="horizontal" size={"small"}>
                  <Button onClick={movePrevWeek}>
                    <ArrowLeftOutlined /> Prev
                  </Button>
                  <Button onClick={moveNextWeek}>
                    Next <ArrowRightOutlined />
                  </Button>
                </Space>
              </Col>
            </Row>
            </StledSpin>

          </>
          <DragAndDropCalendar
            key={calendarStartAt}
            date={defaultDate}
            onNavigate={handleOnNavigate}
            defaultView={Views.WEEK}
            view={currentView}
            onView={(newView) => setCurrentView(newView as 'week' | 'day')}
            events={(displayMode === 'Series' ? myEvents : (displayMode === 'Episodes' ? myEpisodeEvents : mySegmentEvents))}
            eventPropGetter={(event: any) => {
              // Check if this is a paid programming event
              const isPaidProgramming = event.eventType === 'Paid Programming' || event.mediaId || event.isPaidProgramming;
              
              let backgroundColor;
              if (displayMode === 'Episodes') {
                backgroundColor = 'green';
              } else if (displayMode === 'Series' && isPaidProgramming) {
                backgroundColor = '#2f3588'; // Dark blue for paid programming in Series view
              } else if (displayMode === 'Segments' && isPaidProgramming) {
                backgroundColor = '#2f3588'; // Dark blue for paid programming in Segments view
              } else {
                backgroundColor = '#545AA7'; // Default color for regular series events
              }
              
              return { style: { backgroundColor } };
            }}
            draggableAccessor={(event) => (displayMode === 'Series')}
            slotGroupPropGetter={slotGroupPropGetter}
            localizer={localizer}
            min={minTime}
            max={maxTime}
            scrollToTime={isShiftedStart ? moment('1970-01-01T06:00:00').toDate() : minTime}
            onEventDrop={moveEvent}
            onEventResize={resizeEvent}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onRangeChange={onRangeChange}
            dayPropGetter={outsideRangeDayPropGetter}
            views={[Views.WEEK, Views.DAY]}
            popup
            resizable={(displayMode === 'Series')}
            selectable={(displayMode === 'Series')}
            formats={formats}
            toolbar={false}
            step={10}
            timeslots={6} // 10 minutes per slot, 6 slots per hour
          />
         
         <Modal
            title={scheduleNameModal === 'new' ? 'New Schedule' : 'Name this schedule'}
            open={scheduleNameModal !== null}
            onCancel={handleScheduleNameCancel}
            onOk={handleScheduleNameConfirm}
            okText={scheduleNameModal === 'new' ? 'Create' : 'Save'}
            okButtonProps={{ disabled: !scheduleNameInput.trim() }}
            destroyOnClose
          >
            {scheduleNameModal === 'name' && (
              <Typography.Paragraph type="secondary">
                Give this schedule a name before it is saved.
              </Typography.Paragraph>
            )}
            <Input
              autoFocus
              placeholder="Schedule name"
              value={scheduleNameInput}
              onChange={e => setScheduleNameInput(e.target.value)}
              onPressEnter={handleScheduleNameConfirm}
            />
          </Modal>

         <BlockSchedulePickerModal
            visible={showBlockSchedulePickerModal}
            currentSelectedScheduleId={currentBlockScheduleId || ''}
            onClose={(data) => {
              setShowBlockSchedulePickerModal(false);
              if (data) {
                setIsNewDraft(false);
                setCurrentBlockScheduleTitle(data.title);
                setCurrentBlockScheduleId(data.id);
              }
            }}
            onDeleted={(deletedScheduleId) => {
              // Deleting the schedule that is currently loaded would leave the grid
              // editing a row that no longer exists, so fall back to a fresh
              // unnamed draft; its first save asks for a name.
              if (deletedScheduleId === currentBlockScheduleId) {
                startNewDraft(null);
              }
            }}
          />

          <EpisodeEventModal
            visible={showEpisodeModal}
            initialData={episodeModalInitialData}
            onClose={(selectedEpisode) => {
              if(selectedEpisode) {
                // console.log('EpisodeEventModal onClose', selectedEpisode);
                swapEpisodeEvent(selectedEpisode);
              } else {
                console.log('EpisodeEventModal onClose - no data');
              }
              setShowEpisodeModal(false);
            }}
            onDelete={(oldEventID: string) => {
              console.log('EpisodeEventModal onDelete', oldEventID);
            }}
          />
          
          
          <TimeModal
            visible={showTimeModal}
            seriesDropdownValues={seriesDropdownValues}
            initialData={timeModalInitialData}
            eventMode={eventMode}
            setEventMode={setEventMode}
            mediaList={mediaList}
            onClose={(data) => {
              setShowTimeModal(false);
              if (data) {
                const series = data as SeriesEvent;
                addOrUpdateSeriesAsEvent(series);
              }
            }}
            onDelete={(oldEventID) => {
              setMyEvents((prev: any) => prev.filter((ev: any) => ev.id !== oldEventID));
              setShowTimeModal(false);
            }}
          />

          <Modal
            title="Marathon Segment Validation"
            open={showValidationModal}
            onCancel={() => setShowValidationModal(false)}
            width={880}
            footer={[
              <Button key="close" type="primary" onClick={() => setShowValidationModal(false)}>
                Close
              </Button>,
            ]}
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              action={
                <Tooltip title="Copy references">
                  <CopyOutlined
                    onClick={copyValidationReferences}
                    style={{ cursor: 'pointer', fontSize: 16, color: '#8c8c8c' }}
                  />
                </Tooltip>
              }
              message={
                validationBlockedExport
                  ? `Export stopped — ${validationIssues.length} of ${validationChecked} episode(s) have incomplete Marathon segments.`
                  : `${validationIssues.length} of ${validationChecked} episode(s) have incomplete Marathon segments.`
              }
              description={
                validationBlockedExport
                  ? 'These would be dropped from or truncate the Marathon FAST export. Fix the videos (re-slice / restore the S3 file) or remove them from the grid, then export again. Use “Copy references” to grab the list.'
                  : 'These will be dropped from or truncate the Marathon FAST export. Fix the videos (re-slice / restore the S3 file) or remove them before exporting. Use “Copy references” to grab the list.'
              }
            />
            <Table
              size="small"
              rowKey="refId"
              dataSource={validationIssues}
              pagination={validationIssues.length > 10 ? { pageSize: 10 } : false}
              columns={[
                {
                  title: 'Episode',
                  key: 'episode',
                  width: 150,
                  render: (_: any, r: MarathonSegmentIssue) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.refId}</div>
                      {r.title && <div style={{ fontSize: 11, color: '#888' }}>{r.title}</div>}
                    </div>
                  ),
                },
                {
                  title: 'Video',
                  key: 'video',
                  render: (_: any, r: MarathonSegmentIssue) => (
                    <div>
                      <div style={{ wordBreak: 'break-all' }}>{r.fileName || '—'}</div>
                      {r.videoId && (
                        <Typography.Text
                          copyable={{ text: r.videoId }}
                          style={{ fontSize: 10, color: '#aaa' }}
                        >
                          {r.videoId}
                        </Typography.Text>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Segments',
                  key: 'segments',
                  width: 120,
                  render: (_: any, r: MarathonSegmentIssue) => {
                    const exp =
                      r.expectedMin == null
                        ? '?'
                        : r.expectedMin === r.expectedMax
                        ? `${r.expectedMin}`
                        : `${r.expectedMin}-${r.expectedMax}`;
                    const base = `${r.actual} / ${exp}`;
                    // Surface duplicates: more rows than distinct segments, or >1 owning video.
                    return r.totalSegments > r.actual || r.videoCount > 1
                      ? `${base} · ${r.totalSegments} rows${r.videoCount > 1 ? `, ${r.videoCount} vids` : ''}`
                      : base;
                  },
                },
                {
                  title: 'Problem',
                  key: 'problem',
                  render: (_: any, r: MarathonSegmentIssue) => (
                    <div>
                      <Space size={[0, 4]} wrap>
                        {r.reasons.map((reason, i) => (
                          <Tag color="error" key={i}>
                            {reason}
                          </Tag>
                        ))}
                      </Space>
                      {r.segmentationNotes && (
                        <div style={{ fontSize: 11, color: '#a8071a', marginTop: 4, wordBreak: 'break-all' }}>
                          {r.segmentationNotes}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Modal>
        </CalendarDiv>
      </Fragment>
  )
}
